import type { ComponentType } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router';
import { AppHome, PublicOnly, RequireAuth, RequireOnboarded } from '@/components/layout/guards';
import { RootLayout } from '@/components/layout/RootLayout';
import { RouteFallback } from '@/components/layout/RouteFallback';

/** Lazy route helper: every screen is its own chunk. */
const page = (load: () => Promise<{ default: ComponentType }>) => ({
  lazy: async () => ({ Component: (await load()).default }),
});

const routes: RouteObject[] = [
  {
    Component: RootLayout,
    HydrateFallback: RouteFallback,
    children: [
      // Public
      { index: true, ...page(() => import('./pages/landing/LandingPage')) },
      { path: 'design', ...page(() => import('./pages/design/DesignSystemPage')) },
      { path: 'verify-email', ...page(() => import('./pages/auth/VerifyEmailPage')) },
      { path: 'reset-password', ...page(() => import('./pages/auth/ResetPasswordPage')) },
      { path: 'banned', ...page(() => import('./pages/BannedPage')) },
      { path: 'terms', ...page(() => import('./pages/legal/LegalPage')) },
      { path: 'privacy', ...page(() => import('./pages/legal/LegalPage')) },
      { path: 'about', ...page(() => import('./pages/legal/LegalPage')) },
      {
        Component: PublicOnly,
        children: [
          { path: 'signup', ...page(() => import('./pages/auth/SignUpPage')) },
          { path: 'login', ...page(() => import('./pages/auth/SignInPage')) },
          { path: 'forgot-password', ...page(() => import('./pages/auth/ForgotPasswordPage')) },
        ],
      },
      // Signed in
      {
        Component: RequireAuth,
        children: [
          { path: 'app', Component: AppHome },
          { path: 'onboarding/:step?', ...page(() => import('./pages/onboarding/OnboardingPage')) },
          { path: 'admin/:section?', ...page(() => import('./pages/admin/AdminPage')) },
          { path: 'admin/users/:id', ...page(() => import('./pages/admin/AdminUserPage')) },
          {
            Component: RequireOnboarded,
            children: [
              {
                ...page(() => import('./components/layout/AppShell')),
                children: [
                  { path: 'discover', ...page(() => import('./pages/app/DiscoverPage')) },
                  { path: 'matches', ...page(() => import('./pages/app/MatchesPage')) },
                  { path: 'matches/:matchId', ...page(() => import('./pages/app/ChatPage')) },
                  { path: 'people/:id', ...page(() => import('./pages/app/PersonPage')) },
                  { path: 'me', ...page(() => import('./pages/app/MyTapePage')) },
                  { path: 'settings', ...page(() => import('./pages/app/SettingsPage')) },
                ],
              },
            ],
          },
        ],
      },
      { path: '*', ...page(() => import('./pages/NotFoundPage')) },
    ],
  },
];

export const router = createBrowserRouter(routes);
